/**
 * RecoverAccess Page (public)
 *
 * Pre-login self-service account recovery. Calls the public recovery endpoint and,
 * for users who receive a verification code (CONFIRMED), lets them complete the
 * reset inline via Cognito's confirmResetPassword.
 *
 * Route: /recover (public — no ProtectedRoute)
 * Requirements: FR-2, FR-5
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { restClient } from '../api/rest-client';
import { confirmForgotPassword } from '../auth/cognito';
import { useAuth } from '../auth/AuthProvider';

type Phase = 'request' | 'sent' | 'confirm' | 'done';

interface PasswordRequirement {
  text: string;
  test: (p: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  { text: 'At least 8 characters', test: (p) => p.length >= 8 },
  { text: 'One uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { text: 'One lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { text: 'One number (0-9)', test: (p) => /\d/.test(p) },
  { text: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*]/.test(p) },
];

const NEUTRAL_MESSAGE =
  "If an account exists for that email, we've sent recovery instructions.";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function RecoverAccess() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!email.trim()) next.email = 'Email is required';
    else if (!EMAIL_RE.test(email.trim())) next.email = 'Enter a valid email address';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      await restClient.recoverAccess(email.trim());
      setPhase('sent');
    } catch {
      // Do not reveal account state; show a generic, non-identifying error.
      setErrors({ general: 'Something went wrong. Please try again in a few minutes.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!code.trim()) next.code = 'Verification code is required';
    if (!newPassword) {
      next.newPassword = 'New password is required';
    } else if (PASSWORD_REQUIREMENTS.some((r) => !r.test(newPassword))) {
      next.newPassword = 'Password does not meet all requirements';
    }
    if (!confirmPassword) next.confirmPassword = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) next.confirmPassword = 'Passwords do not match';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    try {
      await confirmForgotPassword(email.trim(), code.trim(), newPassword);
      setPhase('done');
    } catch (err: any) {
      const msg = err?.message || 'Failed to reset password';
      if (/code/i.test(msg)) setErrors({ code: msg });
      else if (/requirement/i.test(msg)) setErrors({ newPassword: msg });
      else setErrors({ general: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requirementsMet = PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(newPassword) }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col">
      <header className="bg-primary-500 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold">Blue Clipboard</h1>
          <p className="text-primary-100 mt-1">Recover Access</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 flex-grow flex items-start justify-center">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {errors.general}
            </div>
          )}

          {phase === 'request' && (
            <form onSubmit={handleRequest} className="space-y-4" noValidate>
              <p className="text-sm text-gray-600">
                Enter your email and we'll send instructions to help you regain access.
              </p>
              <div>
                <label htmlFor="recover-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  id="recover-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                  data-testid="recover-email-input"
                  autoComplete="email"
                />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-50"
                data-testid="recover-submit-button"
              >
                {isSubmitting ? 'Sending...' : 'Send recovery instructions'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="w-full text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Back
              </button>
            </form>
          )}

          {phase === 'sent' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
                {NEUTRAL_MESSAGE}
              </div>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  <strong>First time logging in?</strong> Check your email for a temporary password,
                  then log in — you'll be prompted to set a new password.
                </p>
                <p>
                  <strong>Already had a password?</strong> Check your email for a verification code,
                  then enter it below.
                </p>
              </div>
              <button
                type="button"
                onClick={login}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-300"
                data-testid="recover-go-to-login-button"
              >
                Go to Login
              </button>
              <button
                type="button"
                onClick={() => setPhase('confirm')}
                className="w-full text-sm text-primary-600 hover:text-primary-700 underline"
                data-testid="recover-have-code-toggle"
              >
                Have a verification code? Enter it
              </button>
            </div>
          )}

          {phase === 'confirm' && (
            <form onSubmit={handleConfirm} className="space-y-4" noValidate>
              <p className="text-sm text-gray-600">
                Enter the verification code sent to <strong>{email}</strong> and choose a new password.
              </p>
              <div>
                <label htmlFor="recover-code" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  id="recover-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.code ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                  data-testid="recover-code-input"
                  autoComplete="off"
                />
                {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
              </div>
              <div>
                <label htmlFor="recover-new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  id="recover-new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.newPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                  data-testid="recover-new-password-input"
                  autoComplete="new-password"
                />
                {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>}
                {newPassword && (
                  <div className="mt-2 space-y-1">
                    {requirementsMet.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={r.met ? 'text-green-700' : 'text-gray-500'}>
                          {r.met ? '✓' : '○'} {r.text}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="recover-confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  id="recover-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                  data-testid="recover-confirm-password-input"
                  autoComplete="new-password"
                />
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-300 disabled:opacity-50"
                data-testid="recover-confirm-button"
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
              <button
                type="button"
                onClick={() => setPhase('sent')}
                className="w-full text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Back
              </button>
            </form>
          )}

          {phase === 'done' && (
            <div className="space-y-4 text-center">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
                Your password has been reset. You can now log in with your new password.
              </div>
              <button
                type="button"
                onClick={login}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-bold py-3 px-4 rounded-lg focus:outline-none focus:ring-4 focus:ring-primary-300"
                data-testid="recover-go-to-login-button"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
